import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { AppLayout, GlassCard, PageHeader } from '../components/Layout';
import { Button } from '../components/ui/button';
import { 
  Bell, Mail, Shield, Clock, Settings, CheckCircle,
  AlertTriangle, Eye, Activity, Lock, Smartphone
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function NotificationsPage() {
  const { user, getAuthHeader } = useAuth();
  const [activeTab, setActiveTab] = useState('inbox');
  const [deltaNotifications, setDeltaNotifications] = useState(null);
  const [inAppNotifications, setInAppNotifications] = useState([]);
  const [preferences, setPreferences] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sendingDigest, setSendingDigest] = useState(false);
  const [testingEmail, setTestingEmail] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [deltaRes, notifRes, prefsRes] = await Promise.all([
        axios.get(`${API}/notifications/delta`, { ...getAuthHeader(), withCredentials: true }),
        axios.get(`${API}/notifications/in-app`, { ...getAuthHeader(), withCredentials: true }),
        axios.get(`${API}/notifications/preferences`, { ...getAuthHeader(), withCredentials: true })
      ]);
      setDeltaNotifications(deltaRes.data);
      setInAppNotifications(notifRes.data);
      setPreferences(prefsRes.data);
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  const updatePreferences = async (updates) => {
    try {
      const response = await axios.put(`${API}/notifications/preferences`, updates, {
        ...getAuthHeader(),
        withCredentials: true
      });
      setPreferences(response.data);
      toast.success('Preferences updated');
    } catch (error) {
      toast.error('Failed to update preferences');
    }
  };

  const markAsRead = async (notificationId) => {
    try {
      await axios.put(`${API}/notifications/in-app/${notificationId}/read`, {}, {
        ...getAuthHeader(),
        withCredentials: true
      });
      setInAppNotifications(prev => 
        prev.map(n => n.id === notificationId ? { ...n, read: true } : n)
      );
    } catch (error) {
      toast.error('Failed to mark as read');
    }
  };

  const sendWeeklyDigest = async () => {
    try {
      setSendingDigest(true);
      const response = await axios.post(`${API}/notifications/weekly-digest/send`, {}, {
        ...getAuthHeader(),
        withCredentials: true
      });
      if (response.data.status === 'success') {
        toast.success('Weekly digest sent to your email!');
      } else {
        toast.error(response.data.message || 'Failed to send digest');
      }
    } catch (error) {
      toast.error('Failed to send weekly digest');
    } finally {
      setSendingDigest(false);
    }
  };

  const sendTestEmail = async () => {
    try {
      setTestingEmail(true);
      const response = await axios.post(`${API}/notifications/test-email`, 
        { email: user.email },
        { ...getAuthHeader(), withCredentials: true }
      );
      if (response.data.status === 'success') {
        toast.success('Test email sent! Check your inbox.');
      } else {
        toast.error(response.data.message || 'Failed to send test email');
      }
    } catch (error) {
      toast.error('Failed to send test email');
    } finally {
      setTestingEmail(false);
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'text-red-400 bg-red-500/20';
      case 'medium': return 'text-yellow-400 bg-yellow-500/20';
      case 'low': return 'text-green-400 bg-green-500/20';
      default: return 'text-slate-300 bg-slate-500/20';
    }
  };

  return (
    <AppLayout>
      <PageHeader 
        title="Notifications & Alerts"
        description="Stay updated on your progress and important changes"
      />

      {/* Quick Stats */}
      {deltaNotifications && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <GlassCard className="text-center" data-testid="total-changes-card">
            <Bell className="w-8 h-8 mx-auto mb-2 text-blue-400" />
            <p className="text-2xl font-bold text-white">{deltaNotifications.total_changes}</p>
            <p className="text-sm text-slate-300">Total Changes</p>
          </GlassCard>
          <GlassCard className="text-center" data-testid="high-priority-card">
            <AlertTriangle className="w-8 h-8 mx-auto mb-2 text-red-400" />
            <p className="text-2xl font-bold text-white">{deltaNotifications.high_priority_count}</p>
            <p className="text-sm text-slate-300">High Priority</p>
          </GlassCard>
          <GlassCard className="text-center">
            <Mail className="w-8 h-8 mx-auto mb-2 text-emerald-400" />
            <p className="text-2xl font-bold text-white">{preferences?.weekly_digest ? 'On' : 'Off'}</p>
            <p className="text-sm text-slate-300">Weekly Digest</p>
          </GlassCard>
          <GlassCard className="text-center">
            <Shield className="w-8 h-8 mx-auto mb-2 text-purple-400" />
            <p className="text-2xl font-bold text-white">{preferences?.email_enabled ? 'Active' : 'Inactive'}</p>
            <p className="text-sm text-slate-300">Email Alerts</p>
          </GlassCard>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
        {[
          { id: 'delta', label: 'What\'s New', icon: Activity },
          { id: 'inbox', label: 'Inbox', icon: Bell },
          { id: 'preferences', label: 'Preferences', icon: Settings },
        ].map(tab => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              data-testid={`tab-${tab.id}`}
              className={`px-4 py-2 rounded-lg flex items-center gap-2 whitespace-nowrap transition-all ${
                activeTab === tab.id
                  ? 'bg-blue-600 text-white'
                  : 'bg-navy-800 text-slate-300 hover:text-white'
              }`}
            >
              <Icon className="w-4 h-4" />
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* Delta (What's New) Tab */}
      {activeTab === 'delta' && deltaNotifications && (
        <div className="space-y-6">
          <GlassCard>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-white">Changes Since Last Login</h3>
              <p className="text-sm text-slate-300">{deltaNotifications.summary}</p>
            </div>
            
            {deltaNotifications.changes?.length === 0 ? (
              <p className="text-slate-300 text-center py-8">No new changes since your last visit!</p>
            ) : (
              <div className="space-y-3">
                {deltaNotifications.changes?.map((change, i) => (
                  <div 
                    key={change.id || i}
                    data-testid={`change-${i}`}
                    className="flex items-start gap-4 p-4 bg-navy-800/50 rounded-lg hover:bg-navy-800 transition-colors"
                  >
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${getPriorityColor(change.importance)}`}>
                      {change.change_type === 'deadline_approaching' ? <Clock className="w-5 h-5" /> :
                       change.change_type === 'completed' ? <CheckCircle className="w-5 h-5" /> :
                       <Bell className="w-5 h-5" />}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <h4 className="font-semibold text-white">{change.title}</h4>
                        <span className={`px-2 py-0.5 rounded text-xs ${getPriorityColor(change.importance)}`}>
                          {change.importance}
                        </span>
                      </div>
                      <p className="text-slate-300 text-sm mt-1">{change.description}</p>
                      <p className="text-slate-300 text-xs mt-2">Category: {change.category}</p>
                    </div>
                    {change.link && (
                      <Button variant="outline" size="sm" onClick={() => window.location.href = change.link}>
                        View
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </GlassCard>
        </div>
      )}

      {/* Inbox Tab */}
      {activeTab === 'inbox' && (
        <div className="space-y-4">
          {inAppNotifications.length === 0 ? (
            <GlassCard>
              <p className="text-slate-300 text-center py-8">No notifications yet</p>
            </GlassCard>
          ) : (
            inAppNotifications.map((notif, i) => (
              <GlassCard 
                key={notif.id || i}
                data-testid={`notification-${i}`}
                className={`${!notif.read ? 'border-blue-500/50' : ''}`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-3">
                    <div className={`w-3 h-3 rounded-full mt-1.5 ${notif.read ? 'bg-slate-600' : 'bg-blue-500'}`} />
                    <div>
                      <h4 className="font-semibold text-white">{notif.title}</h4>
                      <p className="text-slate-300 text-sm">{notif.message}</p>
                      <p className="text-slate-300 text-xs mt-2">{new Date(notif.created_at).toLocaleString()}</p>
                    </div>
                  </div>
                  {!notif.read && (
                    <Button variant="ghost" size="sm" onClick={() => markAsRead(notif.id)}>
                      Mark Read
                    </Button>
                  )}
                </div>
              </GlassCard>
            ))
          )}
        </div>
      )}

      {/* Preferences Tab */}
      {activeTab === 'preferences' && preferences && (
        <div className="space-y-6">
          <GlassCard>
            <h3 className="text-lg font-semibold text-white mb-4">Email Notifications</h3>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white font-medium">Email Notifications</p>
                  <p className="text-slate-300 text-sm">Receive notifications via email</p>
                </div>
                <button
                  onClick={() => updatePreferences({ email_enabled: !preferences.email_enabled })}
                  data-testid="toggle-email"
                  className={`w-12 h-6 rounded-full transition-colors ${
                    preferences.email_enabled ? 'bg-blue-600' : 'bg-slate-600'
                  }`}
                >
                  <div className={`w-5 h-5 bg-white rounded-full transition-transform ${
                    preferences.email_enabled ? 'translate-x-6' : 'translate-x-0.5'
                  }`} />
                </button>
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white font-medium">Weekly Digest</p>
                  <p className="text-slate-300 text-sm">Get a weekly summary of your progress</p>
                </div>
                <button
                  onClick={() => updatePreferences({ weekly_digest: !preferences.weekly_digest })}
                  data-testid="toggle-digest"
                  className={`w-12 h-6 rounded-full transition-colors ${
                    preferences.weekly_digest ? 'bg-blue-600' : 'bg-slate-600'
                  }`}
                >
                  <div className={`w-5 h-5 bg-white rounded-full transition-transform ${
                    preferences.weekly_digest ? 'translate-x-6' : 'translate-x-0.5'
                  }`} />
                </button>
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white font-medium">Deadline Reminders</p>
                  <p className="text-slate-300 text-sm">Get reminded before deadlines</p>
                </div>
                <button
                  onClick={() => updatePreferences({ deadline_reminders: !preferences.deadline_reminders })}
                  data-testid="toggle-reminders"
                  className={`w-12 h-6 rounded-full transition-colors ${
                    preferences.deadline_reminders ? 'bg-blue-600' : 'bg-slate-600'
                  }`}
                >
                  <div className={`w-5 h-5 bg-white rounded-full transition-transform ${
                    preferences.deadline_reminders ? 'translate-x-6' : 'translate-x-0.5'
                  }`} />
                </button>
              </div>
            </div>
          </GlassCard>

          <GlassCard>
            <h3 className="text-lg font-semibold text-white mb-4">Alert Types</h3>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white font-medium">Financial Alerts</p>
                  <p className="text-slate-300 text-sm">Important financial updates</p>
                </div>
                <button
                  onClick={() => updatePreferences({ financial_alerts: !preferences.financial_alerts })}
                  className={`w-12 h-6 rounded-full transition-colors ${
                    preferences.financial_alerts ? 'bg-blue-600' : 'bg-slate-600'
                  }`}
                >
                  <div className={`w-5 h-5 bg-white rounded-full transition-transform ${
                    preferences.financial_alerts ? 'translate-x-6' : 'translate-x-0.5'
                  }`} />
                </button>
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white font-medium">Application Updates</p>
                  <p className="text-slate-300 text-sm">Status changes on applications</p>
                </div>
                <button
                  onClick={() => updatePreferences({ application_updates: !preferences.application_updates })}
                  className={`w-12 h-6 rounded-full transition-colors ${
                    preferences.application_updates ? 'bg-blue-600' : 'bg-slate-600'
                  }`}
                >
                  <div className={`w-5 h-5 bg-white rounded-full transition-transform ${
                    preferences.application_updates ? 'translate-x-6' : 'translate-x-0.5'
                  }`} />
                </button>
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white font-medium">Scholarship Matches</p>
                  <p className="text-slate-300 text-sm">New scholarship opportunities</p>
                </div>
                <button
                  onClick={() => updatePreferences({ scholarship_matches: !preferences.scholarship_matches })}
                  className={`w-12 h-6 rounded-full transition-colors ${
                    preferences.scholarship_matches ? 'bg-blue-600' : 'bg-slate-600'
                  }`}
                >
                  <div className={`w-5 h-5 bg-white rounded-full transition-transform ${
                    preferences.scholarship_matches ? 'translate-x-6' : 'translate-x-0.5'
                  }`} />
                </button>
              </div>
            </div>
          </GlassCard>

          <GlassCard>
            <h3 className="text-lg font-semibold text-white mb-4">Test Email</h3>
            <p className="text-slate-300 mb-4">Send a test email to verify your email configuration is working.</p>
            <div className="flex gap-4">
              <Button 
                onClick={sendTestEmail} 
                disabled={testingEmail}
                data-testid="send-test-email"
              >
                {testingEmail ? 'Sending...' : 'Send Test Email'}
              </Button>
              <Button 
                variant="outline" 
                onClick={sendWeeklyDigest}
                disabled={sendingDigest}
                data-testid="send-digest"
              >
                {sendingDigest ? 'Sending...' : 'Send Weekly Digest Now'}
              </Button>
            </div>
          </GlassCard>
        </div>
      )}
    </AppLayout>
  );
}
